import { createContext, useContext, onMount, onCleanup, createEffect } from "solid-js";
import { createStore } from "solid-js/store";
import socket from "./socket";
import { useConnection } from "./ConnectionManager";
import { useOfflineData } from "./OfflineDataManager";

const UserContext = createContext();

// retrieve query params 'jwt' and 'page'
const urlParams = new URLSearchParams(window.location.search);
const jwt = urlParams.get('jwt') || localStorage.getItem('jwt');
// Get the page from URL or default to homepage
const initialPage = urlParams.get('page') || '/';
if(jwt) {
  localStorage.setItem('jwt', jwt);
}
if(urlParams.get('jwt')) {
  const newUrl = initialPage === '/' ? 
    '/' : 
    `/?page=${encodeURIComponent(initialPage)}`;
  
  //remove jwt from url but keep the page parameter if needed
  window.history.replaceState({}, document.title, newUrl);
}

export function UserContextProvider(props: any) {
  // Get connection status from ConnectionManager
  const [connectionStore] = useConnection();
  
  // Get offline data manager
  const offlineDataManager = useOfflineData();
  
  const [userStore, setUserStore] = createStore({
    isConnected: false,
    isAuthenticated: false,
    projects: [],
    page: initialPage, // Use the page from URL
    username: null,
    fullname: null,
    email: null,
    role: null,
    isAdmin: false,
    loadingUser: true,
    conventions: [], // Only used for non-admin user's own conventions
    promos_by_id: {},
    conventionStructure: { fields: [] }, // Default empty structure
  });
  
  // Handler for popstate events (browser back/forward)
  const handlePopstate = () => {
    // Extract the page from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page') || '/';
    
    // Update the store with the new page
    setUserStore({page});
  };
  
  // Effect to react to connection status changes
  createEffect(() => {
    // Update user store with offline status
    setUserStore('isOffline', connectionStore.isOffline);
    
    // Load cached user data when going offline
    if (connectionStore.isOffline && userStore.loadingUser) {
      loadUserDataFromCache();
    }
  });
  
  // Load user data from cache
  const loadUserDataFromCache = async () => {
    const userData = await offlineDataManager.loadUserData();
    if (userData) {
      setUserStore({
        ...userData,
        loadingUser: false
      });
    } else {
      setUserStore({ loadingUser: false });
    }
  };

  createEffect(() => {
    if(userStore.isAuthenticated && !connectionStore.isOffline) {
      socket.emit("get-convention-structure");
    }
  });
  
  onMount(() => {
    console.log('Mounted UserContext');
    
    // Only try socket connection if online
    if (!connectionStore.isOffline) {
      if (jwt) {
        socket.emit("authenticate", jwt);
      } else {
        setUserStore({ loadingUser: false });
      }
    }
    
    // Handle browser back/forward buttons
    window.addEventListener('popstate', handlePopstate);
    
    // Register socket event handlers
    socket.on("projects", (projects: any) => {
      setUserStore({projects});
    });
    
    socket.on("authenticated", ({username, fullname, email, role, promo_id, id, student_number, additional_info}: any) => {
      console.log('connected as', {username, fullname, email, role, promo_id, id, student_number, additional_info});
      const isAdmin = role === 'admin';
      const userData = {
        isAuthenticated: true,
        username, 
        fullname, 
        email, 
        role, 
        promo_id, 
        id, 
        isAdmin, 
        loadingUser: false,
        student_number,
        additional_info
      };
      setUserStore(userData);
      
      // Save user data for offline access
      offlineDataManager.saveUserData(userData);
    });
    
    socket.on("unauthorized", (error: any) => {
      console.log({error});
      localStorage.removeItem('jwt');
      setUserStore({
        username: null,
        role: null,
        isAdmin: false,
        loadingUser: false,
      });
    });
    
    socket.on("conventions-list", (conventions: any) => {
      console.log('received conventions-list', conventions);
      setUserStore({ conventions });
    });
    
    socket.on("promo-list", (promos: any) => {
      console.log('received promo-list', promos);
      // Convert the array to an object with id key for easier lookup
      const promos_by_id = promos.reduce((acc: any, promo: any) => {
        acc[promo.id] = promo;
        return acc;
      }, {});
      setUserStore({ promos_by_id });
    });
    
    socket.on("select-promo-done", (promo_id: any) => {
      setUserStore((store: any) => ({...store, promo_id}));
    });
    
    // Handle convention structure from server
    socket.on("convention-structure", (structure: any) => {
      console.log('received convention structure', structure);
      setUserStore({ conventionStructure: structure });
    });
    
    // Cleanup on unmount
    onCleanup(() => {
      window.removeEventListener('popstate', handlePopstate);
    });
  });
  
  const actions = {
    createProject: (fields: any) => {
      console.log('createProject', fields);
    },
    setPage: (page: any) => {
      console.log('setPage', page);
      
      // Update the URL to reflect the current page
      if (page === '/') {
        // For homepage, remove the page parameter entirely
        window.history.pushState({}, document.title, "/");
      } else {
        // For other pages, add/update the page parameter
        window.history.pushState({}, document.title, `/?page=${encodeURIComponent(page)}`);
      }
      
      setUserStore({page});
    },
    createConvention: () => {
      if (connectionStore.isOffline) {
        console.log('Cannot create convention in offline mode');
        return;
      }
      socket.emit("create-convention");
    },
    updateConvention: (id: any, info: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot update convention in offline mode');
        return;
      }
      socket.emit("update-convention", { id, info });
    },
    changeConventionStatus: (id: any, status: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot change convention status in offline mode');
        return;
      }
      socket.emit("change-convention-status", { id, status });
    },
    deleteConvention: (id: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot delete convention in offline mode');
        return;
      }
      socket.emit("delete-convention", id);
    },
    selectPromo: (promo_id: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot select promo in offline mode');
        return;
      }
      socket.emit("select-promo", promo_id);
    },
    leavePromo: () => {
      if (connectionStore.isOffline) {
        console.log('Cannot leave promo in offline mode');
        return;
      }
      // Set promo_id to null in user store and emit event to server
      setUserStore((store: any) => ({...store, promo_id: null}));
      socket.emit("leave-promo");
    },
    updateUserProfile: (fields: any) => {
      if (connectionStore.isOffline) {
        console.log('Cannot update profile in offline mode');
        return Promise.reject(new Error('Cannot update profile in offline mode'));
      }
      
      return new Promise((resolve, reject) => {
        const onSuccess = (data: any) => {
          socket.off('update-profile-success', onSuccess);
          socket.off('update-profile-error', onError);
          setUserStore(data);
          // Save updated user data for offline access
          offlineDataManager.saveUserData(data);
          resolve(data);
        };
        
        const onError = (error: any) => {
          socket.off('update-profile-success', onSuccess);
          socket.off('update-profile-error', onError);
          reject(error);
        };
        
        socket.on('update-profile-success', onSuccess);
        socket.on('update-profile-error', onError);
        
        socket.emit("update-profile", fields);
      });
    },
  }
  return (
    <UserContext.Provider value={[userStore, setUserStore, actions]}>
      {props.children}
    </UserContext.Provider>
  );
}

export function useUserContext() { 
  return useContext(UserContext); 
}
